import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Header } from '@components/Header';
import DiagonalArrow from '@components/icons/DiagonalArrow.svg';
import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { PagePagination } from '@components/core-ui/Pagination';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { formatCentsPrice } from '@helpers/formatCentsPrice';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { downloadBillingTransactionsCsv } from '@helpers/downloadBillingTransactionsCsv';
import { AudioHourRateCard } from '@pages/User/BillingAccountDetailsPage/components/SubscriptionsWrapper/Cards/AudioHourRateCard';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { billingKeys } from 'app/queries/billing/billingKeys';
import { BillingResourceError, getBillingResourceError } from 'app/helpers/getBillingResourceError';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { createFetchBillingAccountQueryFn } from './api/fetchBillingAccount';
import { createFetchBillingAccountPaymentMethodsQueryFn } from './api/fetchBillingAccountPaymentMethods';
import { createFetchBillingAccountTransactionsQueryFn } from './api/fetchBillingAccountTransactions';
import { CreditBalanceCard } from './components/CreditBalanceCard';
import { PaymentMethodCard } from './components/PaymentMethodCard';
import { SubscriptionsWrapper } from './components/SubscriptionsWrapper';
import { TransactionType } from './components/TransactionType';
import { CardWrapper } from './styles/CardWrapper';
import { Cell } from './styles/Cell';
import { ReceiptUrl } from './styles/ReceiptUrl';
import { StyledTitle } from './styles/StyledTitle';
import { Wrapper } from './styles/Wrapper';

export { StyledTitle } from './styles/StyledTitle';

const BillingResourceErrorMessage = ({
  error,
  hasLoaded,
  isLoading,
  onRetry,
}: {
  error: BillingResourceError;
  hasLoaded: boolean;
  isLoading: boolean;
  onRetry: () => Promise<void>;
}) => {
  if (error.kind === 'none') {
    return null;
  }

  if (error.kind === 'forbidden') {
    return <p className="mt-4 text-sm text-foreground">Access denied</p>;
  }

  return (
    <div className="mt-4 flex items-center gap-3 text-sm text-foreground" role="alert">
      <span>{hasLoaded ? "Couldn't refresh — showing previously loaded data" : "Couldn't load billing details"}</span>
      <Button disabled={isLoading} onClick={() => void onRetry()}>
        Retry
      </Button>
    </div>
  );
};

export const AdminBillingAccountDetailsPage = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id?: string }>();
  const baId = id ?? '';

  const { page, pageSize, setPage, setPageSize } = useListQueryParams({
    defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
  });

  const accountQuery = useQuery({
    queryKey: billingKeys.account(baId),
    queryFn: createFetchBillingAccountQueryFn(call),
    enabled: baId !== '',
  });
  const paymentMethodsQuery = useQuery({
    queryKey: billingKeys.paymentMethodsByAccount(baId),
    queryFn: createFetchBillingAccountPaymentMethodsQueryFn(call),
    enabled: baId !== '',
  });
  const transactionsQuery = useQuery({
    queryKey: billingKeys.transactionsByAccount(baId),
    queryFn: createFetchBillingAccountTransactionsQueryFn(call),
    enabled: baId !== '',
  });

  const exportMutation = useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        queryKey: billingKeys.transactionsByAccount(baId),
        queryFn: createFetchBillingAccountTransactionsQueryFn(call),
        staleTime: 0,
      }),
    onSuccess: (transactions) => {
      void downloadBillingTransactionsCsv(transactions.transactions);
    },
  });

  const billingAccount = accountQuery.data;
  const allPaymentMethods = paymentMethodsQuery.data;
  const paymentMethods = allPaymentMethods
    ? {
        default: allPaymentMethods.find((item) => item.default) || allPaymentMethods[0],
        all: allPaymentMethods,
      }
    : undefined;
  const allTransactions = transactionsQuery.data?.transactions ?? [];
  const data = {
    count: allTransactions.length,
    data: allTransactions.slice(pageSize * page - pageSize, pageSize * page),
  };

  const detailsError: BillingResourceError =
    accountQuery.isError || paymentMethodsQuery.isError
      ? getBillingResourceError(accountQuery.error ?? paymentMethodsQuery.error)
      : { kind: 'none' };
  const transactionsError: BillingResourceError = transactionsQuery.isError
    ? getBillingResourceError(transactionsQuery.error)
    : { kind: 'none' };
  const detailsLoaded = accountQuery.data !== undefined && paymentMethodsQuery.data !== undefined;
  const transactionsLoaded = transactionsQuery.data !== undefined;
  const detailsLoading = accountQuery.isFetching || paymentMethodsQuery.isFetching;
  const transactionsLoading = transactionsQuery.isFetching || exportMutation.isPending;
  const isLoading = detailsLoading || transactionsLoading;

  const load = async () => {
    await Promise.all([accountQuery.refetch(), paymentMethodsQuery.refetch()]);
  };

  const loadData = async () => {
    await transactionsQuery.refetch();
  };

  const reloadCards = () => {
    void queryClient.invalidateQueries({ queryKey: billingKeys.account(baId) });
    void queryClient.invalidateQueries({ queryKey: billingKeys.paymentMethodsByAccount(baId) });
    void queryClient.invalidateQueries({ queryKey: billingKeys.transactionsByAccount(baId) });
  };

  const onExportCsv = () => {
    if (!exportMutation.isPending) {
      exportMutation.mutate();
    }
  };

  if (!id || (!billingAccount && detailsError.kind === 'none')) {
    return (
      <>
        <Helmet>
          <title>Billing Account Details - AutoScript Admin</title>
        </Helmet>
        <Header title="Billing Account Details" />
        <LinearProgressLoader active={isLoading} />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Billing Account Details - AutoScript Admin</title>
      </Helmet>
      <Header
        title="Billing Account Details"
        buttonLabel={data.data?.length ? 'Export CSV' : undefined}
        buttonOnClick={data.data?.length ? () => void onExportCsv() : undefined}
      />

      <LinearProgressLoader active={isLoading} />
      <Wrapper>
        <CardWrapper>
          <BillingResourceErrorMessage
            error={detailsError}
            hasLoaded={detailsLoaded}
            isLoading={detailsLoading}
            onRetry={load}
          />
          {detailsError.kind === 'none' || (detailsError.kind === 'retryable' && detailsLoaded) ? (
            <>
              <PaymentMethodCard
                method={paymentMethods?.default}
                billingAccount={billingAccount}
                canManageBillingAccount={false}
                reloadCards={reloadCards}
              />
              <CreditBalanceCard
                billingAccount={billingAccount}
                canManageBillingAccount={false}
                reloadCards={reloadCards}
              />
              <AudioHourRateCard billingAccount={billingAccount} />
            </>
          ) : null}
        </CardWrapper>

        <SubscriptionsWrapper
          billingAccount={billingAccount}
          reloadPage={reloadCards}
          hasPaymentMethod={!!paymentMethods?.default}
        />

        <StyledTitle className="mt-8">Transactions</StyledTitle>
        <BillingResourceErrorMessage
          error={transactionsError}
          hasLoaded={transactionsLoaded}
          isLoading={transactionsLoading}
          onRetry={loadData}
        />
        {transactionsError.kind === 'none' || (transactionsError.kind === 'retryable' && transactionsLoaded) ? (
          <TableWrapper>
            <Table>
              <Thead>
                <Tr>
                  <Th style={{ width: '170px', maxWidth: '170px' }}>Created</Th>
                  <Th>Title</Th>
                  <Th style={{ width: '90px', maxWidth: '90px' }}>Amount</Th>
                  <Th style={{ width: '90px', maxWidth: '90px' }}>Type</Th>
                  <Th style={{ width: '85px', maxWidth: '85px' }}>Receipt Url</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(data.data || []).map((transaction) => (
                  <Tr key={transaction.id}>
                    <Td>
                      <p className="my-1">{getAmericanTime(transaction.createdAt)}</p>
                    </Td>
                    <Td>
                      <Cell>
                        <p className="my-1">
                          {transaction.title} <br />
                          <small className="text-858dbd text-xs">
                            Charge ID: {transaction.stripeChargeId ?? transaction.id}
                          </small>
                        </p>
                      </Cell>
                    </Td>
                    <Td>
                      <p className="my-1">{formatCentsPrice(transaction.amount)}</p>
                    </Td>
                    <Td>
                      <TransactionType className="my-1" type={transaction.code.name} />
                    </Td>
                    <Td>
                      <div className="my-1">
                        {transaction.stripeReceiptUrl ? (
                          <ReceiptUrl href={transaction.stripeReceiptUrl} target="_blank" rel="noreferrer">
                            Receipt{' '}
                            <div className="mt-2 ml-1">
                              <DiagonalArrow />
                            </div>
                          </ReceiptUrl>
                        ) : null}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableWrapper>
        ) : null}
        <PagePagination
          totalItemsCount={data.count ?? 0}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </Wrapper>
    </>
  );
};
